import { uploadWorkspaceFile, type WorkspaceFileUploadResponse } from './backendAdapter'

export interface OrderedUploadResult {
  fileName: string
  ok: boolean
  value?: WorkspaceFileUploadResponse
}

/**
 * Upload files one at a time, preserving the order given.
 *
 * The backend appends each file's extracted text to the workspace chart in
 * arrival order, and the physician's file order is meaningful (the ED signout
 * is typically placed first). Intake agents search the whole chart for the
 * signout rather than relying on its position, but preserving upload order
 * keeps the assembled chart faithful to what the physician provided. Parallel
 * uploads land in completion order and scramble it — this must stay sequential.
 *
 * A failed upload is recorded (ok: false) and does not block later files.
 */
export const uploadFilesInOrder = async (
  workspaceId: string,
  files: File[],
  { upload = uploadWorkspaceFile }: { upload?: typeof uploadWorkspaceFile } = {}
): Promise<OrderedUploadResult[]> => {
  const results: OrderedUploadResult[] = []
  for (const file of files) {
    try {
      results.push({ fileName: file.name, ok: true, value: await upload(workspaceId, file) })
    } catch (err) {
      console.error(`[Intake] Failed to upload ${file.name}:`, err)
      results.push({ fileName: file.name, ok: false })
    }
  }
  return results
}

export const failedFileNames = (results: OrderedUploadResult[]): string[] =>
  results.filter(r => !r.ok).map(r => r.fileName)
